import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, X, Check, Languages, Plus, Link, Image, BadgeCheck, ChevronDown, ChevronUp, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { carsApi, mediaApi, settingsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CAR_FEATURES } from '@/lib/carFeatures';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCarForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editLang, setEditLang] = useState('de');
  const [imageUrls, setImageUrls] = useState(['']);
  const [imageMode, setImageMode] = useState('upload');
  const [expandedCategories, setExpandedCategories] = useState({});
  // willhaben import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  
  // Settings for dynamic categories
  const [categories, setCategories] = useState({
    fuel_types: ['Benzin', 'Diesel', 'Elektro', 'Hybrid', 'Plugin-Hybrid'],
    transmissions: ['Automatik', 'Schaltgetriebe'],
    drivetrains: ['Frontantrieb', 'Heckantrieb', 'Allrad', '4x4'],
    colors: ['Schwarz', 'Weiß', 'Silber', 'Grau', 'Blau', 'Rot']
  });

  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    price: '',
    fuel_type: 'Benzin',
    color: '',
    horsepower: '',
    doors: '2',
    transmission: 'Automatik',
    drivetrain: 'Frontantrieb',
    year: new Date().getFullYear().toString(),
    description: '',
    description_en: '',
    images: [],
    is_active: true,
    is_featured: false,
    is_sold: false,
    // New fields
    internal_id: '',
    location: '',
    first_registration_month: '',
    first_registration_year: '',
    mileage: '',
    vehicle_type: 'used',
    previous_owners: '',
    features: [],
    // Extended technical specs
    model_specification: '',
    emission_standard: '',
    engine_volume: '',
    cylinders: '',
    seats: '',
    source_url: ''
  });

  // Toggle feature selection
  const toggleFeature = (featureKey) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureKey)
        ? prev.features.filter(f => f !== featureKey)
        : [...prev.features, featureKey]
    }));
  };

  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  useEffect(() => {
    loadCategories();
    if (isEditing) {
      loadCar();
    }
  }, [id]);
  
  const loadCategories = async () => {
    try {
      const response = await settingsApi.get();
      const settings = response.data;
      setCategories({
        fuel_types: settings.car_fuel_types || ['Benzin', 'Diesel', 'Elektro', 'Hybrid', 'Plugin-Hybrid'],
        transmissions: settings.car_transmissions || ['Automatik', 'Schaltgetriebe'],
        drivetrains: settings.car_drivetrains || ['Frontantrieb', 'Heckantrieb', 'Allrad', '4x4'],
        colors: settings.car_colors || ['Schwarz', 'Weiß', 'Silber', 'Grau', 'Blau', 'Rot']
      });
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadCar = async () => {
    setLoading(true);
    try {
      const response = await carsApi.getById(id);
      const car = response.data;
      setFormData({
        brand: car.brand,
        model: car.model,
        price: car.price.toString(),
        fuel_type: car.fuel_type,
        color: car.color,
        horsepower: car.horsepower.toString(),
        doors: car.doors.toString(),
        transmission: car.transmission,
        drivetrain: car.drivetrain || 'Frontantrieb',
        year: car.year.toString(),
        description: car.description || '',
        description_en: car.description_en || '',
        images: car.images || [],
        is_active: car.is_active,
        is_featured: car.is_featured,
        is_sold: car.is_sold || false,
        // New fields
        internal_id: car.internal_id || '',
        location: car.location || '',
        first_registration_month: car.first_registration_month?.toString() || '',
        first_registration_year: car.first_registration_year?.toString() || '',
        mileage: car.mileage?.toString() || '',
        vehicle_type: car.vehicle_type || 'used',
        previous_owners: car.previous_owners?.toString() || '',
        features: car.features || [],
        model_specification: car.model_specification || '',
        emission_standard: car.emission_standard || '',
        engine_volume: car.engine_volume || '',
        cylinders: car.cylinders || '',
        seats: car.seats || '',
        source_url: car.source_url || ''
      });
    } catch (error) {
      toast.error('Fehler beim Laden des Fahrzeugs');
      navigate('/admin/cars');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        horsepower: parseInt(formData.horsepower),
        doors: parseInt(formData.doors),
        year: parseInt(formData.year),
        // New fields
        mileage: formData.mileage ? parseInt(formData.mileage) : null,
        first_registration_month: formData.first_registration_month ? parseInt(formData.first_registration_month) : null,
        first_registration_year: formData.first_registration_year ? parseInt(formData.first_registration_year) : null,
        previous_owners: formData.previous_owners ? parseInt(formData.previous_owners) : null,
        engine_volume: formData.engine_volume ? parseInt(formData.engine_volume) : null,
        cylinders: formData.cylinders ? parseInt(formData.cylinders) : null,
        seats: formData.seats ? parseInt(formData.seats) : null,
        emission_standard: formData.emission_standard || null,
        model_specification: formData.model_specification || null,
        source_url: formData.source_url || null
      };

      if (isEditing) {
        await carsApi.update(id, data);
        toast.success('Auto aktualisiert');
      } else {
        await carsApi.create(data);
        toast.success('Auto erstellt');
      }

      navigate('/admin/cars');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(file => mediaApi.upload(file));

      const responses = await Promise.all(uploadPromises);
      const urls = responses.map(r => r.data.url);

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...urls]
      }));
      toast.success(`${files.length} Bild(er) hochgeladen`);
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addUrlField = () => {
    setImageUrls(prev => [...prev, '']);
  };

  const removeUrlField = (index) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const updateUrlField = (index, value) => {
    setImageUrls(prev => prev.map((url, i) => i === index ? value : url));
  };

  const addUrlsToImages = () => {
    const validUrls = imageUrls.filter(url => url.trim() && isValidUrl(url.trim()));
    if (validUrls.length === 0) {
      toast.error('Bitte geben Sie mindestens eine gültige URL ein');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...validUrls.map(url => url.trim())]
    }));
    setImageUrls(['']);
    toast.success(`${validUrls.length} Bild(er) hinzugefügt`);
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return string.startsWith('http://') || string.startsWith('https://');
    } catch (_) {
      return false;
    }
  };

  const getImageUrl = (url) => {
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const handleWillhabenImport = async () => {
    if (!importUrl || !importUrl.includes('willhaben.at')) {
      toast.error('Bitte eine gültige willhaben.at URL eingeben');
      return;
    }
    setImportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/cars/import/willhaben`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ url: importUrl })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const d = await res.json();
      setFormData(prev => ({
        ...prev,
        brand: d.brand || prev.brand,
        model: d.model || prev.model,
        model_specification: d.model_specification || '',
        price: d.price != null ? String(d.price) : prev.price,
        fuel_type: d.fuel_type || prev.fuel_type,
        color: d.color || prev.color,
        horsepower: d.horsepower ? String(d.horsepower) : prev.horsepower,
        doors: d.doors ? String(d.doors) : prev.doors,
        transmission: d.transmission || prev.transmission,
        drivetrain: d.drivetrain || prev.drivetrain,
        year: d.year ? String(d.year) : prev.year,
        first_registration_month: d.first_registration_month ? String(d.first_registration_month) : prev.first_registration_month,
        first_registration_year: d.first_registration_year ? String(d.first_registration_year) : prev.first_registration_year,
        mileage: d.mileage != null ? String(d.mileage) : prev.mileage,
        previous_owners: d.previous_owners != null ? String(d.previous_owners) : prev.previous_owners,
        location: d.location || prev.location,
        description: d.description || prev.description,
        vehicle_type: d.vehicle_type || prev.vehicle_type,
        features: Array.isArray(d.features) && d.features.length ? d.features : prev.features,
        images: Array.isArray(d.images) && d.images.length ? [...prev.images, ...d.images] : prev.images,
        engine_volume: d.engine_volume ? String(d.engine_volume) : '',
        cylinders: d.cylinders ? String(d.cylinders) : '',
        seats: d.seats ? String(d.seats) : '',
        emission_standard: d.emission_standard || '',
        source_url: importUrl,
      }));
      toast.success(`Import erfolgreich: ${d.brand} ${d.model} — ${(d.images || []).length} Bilder übernommen`);
      setShowImportModal(false);
      setImportUrl('');
    } catch (e) {
      toast.error(`Import fehlgeschlagen: ${e.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-4xl" data-testid="car-form">
      {/* Header */}
      <div className="flex items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => navigate('/admin/cars')}
          className="p-2 hover:bg-[#F9F9F7] rounded"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl">
            {isEditing ? 'Auto bearbeiten' : 'Neues Auto'}
          </h1>
          <p className="text-[#666666] text-sm">
            {isEditing ? 'Fahrzeugdaten aktualisieren' : 'Neues Fahrzeug hinzufügen'}
          </p>
        </div>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg text-sm font-medium shadow-sm transition-all"
            data-testid="willhaben-import-btn"
          >
            <Download size={16} />
            Von willhaben importieren
          </button>
        )}
      </div>

      {/* willhaben Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="willhaben-import-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white flex items-center justify-center">
                <Download size={20} />
              </div>
              <div>
                <h3 className="font-heading text-lg">Von willhaben importieren</h3>
                <p className="text-xs text-[#666666]">Alle Daten & Bilder werden automatisch übernommen</p>
              </div>
            </div>

            <label className="overline block mb-2">willhaben.at Fahrzeug-URL</label>
            <input
              type="url"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://www.willhaben.at/iad/gebrauchtwagen/d/auto/..."
              className="input-premium w-full mb-3"
              disabled={importLoading}
              data-testid="willhaben-import-url-input"
              autoFocus
            />

            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 mb-4">
              <strong>Hinweis:</strong> Der Import lädt bis zu 30 Bilder in unseren Speicher (R2). Das dauert je nach Anzahl der Bilder <strong>5–20 Sekunden</strong>. Bitte warten und das Fenster nicht schließen.
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => { setShowImportModal(false); setImportUrl(''); }}
                disabled={importLoading}
                className="px-4 py-2 border border-[#E5E5E5] text-[#666666] rounded hover:bg-[#F9F9F7] text-sm disabled:opacity-50"
                data-testid="willhaben-cancel-btn"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleWillhabenImport}
                disabled={importLoading || !importUrl}
                className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded text-sm font-medium hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="willhaben-load-btn"
              >
                {importLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Lädt Daten & Bilder…
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Daten laden
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Basic Info */}
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8">
        <h2 className="font-heading text-xl mb-6">Grunddaten</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-2">Interne ID</label>
            <input
              type="text"
              value={formData.internal_id}
              onChange={(e) => setFormData({ ...formData, internal_id: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              placeholder="z.B. FZG-2024-001"
              data-testid="car-internal-id"
            />
          </div>
          <div>
            <label className="overline block mb-2">Fahrzeugtyp *</label>
            <div className="flex gap-4 mt-2">
              {[
                { value: 'new', label: 'Neuwagen' },
                { value: 'used', label: 'Gebrauchtwagen' },
                { value: 'demo', label: 'Vorführwagen' }
              ].map((type) => (
                <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="vehicle_type"
                    value={type.value}
                    checked={formData.vehicle_type === type.value}
                    onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                    className="w-4 h-4 text-gold accent-gold"
                  />
                  <span className="text-sm">{type.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Marke *</label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              required
              data-testid="car-brand"
            />
          </div>
          <div>
            <label className="overline block mb-2">Modell *</label>
            <input
              type="text"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              required
              data-testid="car-model"
            />
          </div>
          <div>
            <label className="overline block mb-2">Preis (€) *</label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              required
              data-testid="car-price"
            />
          </div>
          <div>
            <label className="overline block mb-2">Baujahr *</label>
            <input
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              min="1900"
              max={new Date().getFullYear() + 1}
              required
              data-testid="car-year"
            />
          </div>
          <div>
            <label className="overline block mb-2">Erstzulassung</label>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={formData.first_registration_month}
                onChange={(e) => setFormData({ ...formData, first_registration_month: e.target.value })}
                className="input-premium w-full border border-[#E5E5E5]"
              >
                <option value="">Monat</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2000, i, 1).toLocaleString('de', { month: 'long' })}
                  </option>
                ))}
              </select>
              <select
                value={formData.first_registration_year}
                onChange={(e) => setFormData({ ...formData, first_registration_year: e.target.value })}
                className="input-premium w-full border border-[#E5E5E5]"
              >
                <option value="">Jahr</option>
                {[...Array(30)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Kilometerstand</label>
            <input
              type="number"
              value={formData.mileage}
              onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              placeholder="z.B. 50000"
              min="0"
              data-testid="car-mileage"
            />
          </div>
          <div>
            <label className="overline block mb-2">Vorbesitzer</label>
            <input
              type="number"
              value={formData.previous_owners}
              onChange={(e) => setFormData({ ...formData, previous_owners: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              placeholder="z.B. 1"
              min="0"
              data-testid="car-previous-owners"
            />
          </div>
          <div>
            <label className="overline block mb-2">Standort</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              placeholder="z.B. Wien, Autohaus Hauptstraße"
              data-testid="car-location"
            />
          </div>
        </div>
      </div>

      {/* Technical Details */}
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8">
        <h2 className="font-heading text-xl mb-6">Technische Daten</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="overline block mb-2">Kraftstoff</label>
            <Select
              value={formData.fuel_type}
              onValueChange={(value) => setFormData({ ...formData, fuel_type: value })}
            >
              <SelectTrigger className="w-full" data-testid="car-fuel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.fuel_types.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="overline block mb-2">Getriebe</label>
            <Select
              value={formData.transmission}
              onValueChange={(value) => setFormData({ ...formData, transmission: value })}
            >
              <SelectTrigger className="w-full" data-testid="car-transmission">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.transmissions.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="overline block mb-2">Antrieb</label>
            <Select
              value={formData.drivetrain}
              onValueChange={(value) => setFormData({ ...formData, drivetrain: value })}
            >
              <SelectTrigger className="w-full" data-testid="car-drivetrain">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.drivetrains.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="overline block mb-2">Leistung (PS) *</label>
            <input
              type="number"
              value={formData.horsepower}
              onChange={(e) => setFormData({ ...formData, horsepower: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              required
              data-testid="car-hp"
            />
          </div>
          <div>
            <label className="overline block mb-2">Türen</label>
            <Select
              value={formData.doors}
              onValueChange={(value) => setFormData({ ...formData, doors: value })}
            >
              <SelectTrigger className="w-full" data-testid="car-doors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3</SelectItem>
                <SelectItem value="4">4</SelectItem>
                <SelectItem value="5">5</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="overline block mb-2">Farbe *</label>
            <Select
              value={formData.color}
              onValueChange={(value) => setFormData({ ...formData, color: value })}
            >
              <SelectTrigger className="w-full" data-testid="car-color">
                <SelectValue placeholder="Farbe wählen" />
              </SelectTrigger>
              <SelectContent>
                {categories.colors.map((color) => (
                  <SelectItem key={color} value={color}>{color}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="overline block mb-2">Modellbezeichnung</label>
            <input
              type="text"
              value={formData.model_specification}
              onChange={(e) => setFormData({ ...formData, model_specification: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              placeholder="z.B. Airscape Faltdach Shine"
              data-testid="car-model-specification"
            />
          </div>
          <div>
            <label className="overline block mb-2">Sitze</label>
            <input
              type="number"
              value={formData.seats}
              onChange={(e) => setFormData({ ...formData, seats: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              placeholder="z.B. 5"
              min="1"
              max="9"
              data-testid="car-seats"
            />
          </div>
          <div>
            <label className="overline block mb-2">Hubraum (ccm)</label>
            <input
              type="number"
              value={formData.engine_volume}
              onChange={(e) => setFormData({ ...formData, engine_volume: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              placeholder="z.B. 1998"
              min="0"
              data-testid="car-engine-volume"
            />
          </div>
          <div>
            <label className="overline block mb-2">Zylinder</label>
            <input
              type="number"
              value={formData.cylinders}
              onChange={(e) => setFormData({ ...formData, cylinders: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              placeholder="z.B. 4"
              min="0"
              max="16"
              data-testid="car-cylinders"
            />
          </div>
          <div>
            <label className="overline block mb-2">Emissionsklasse</label>
            <input
              type="text"
              value={formData.emission_standard}
              onChange={(e) => setFormData({ ...formData, emission_standard: e.target.value })}
              className="input-premium w-full border border-[#E5E5E5]"
              placeholder="z.B. EURO 6"
              data-testid="car-emission-standard"
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl">Beschreibung</h2>
          <div className="flex items-center bg-[#F9F9F7] rounded-lg p-1">
            <button
              type="button"
              onClick={() => setEditLang('de')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center gap-1.5 ${
                editLang === 'de' 
                  ? 'bg-white text-[#0A0A0A] shadow-sm' 
                  : 'text-[#666666] hover:text-[#0A0A0A]'
              }`}
            >
              🇩🇪 DE
            </button>
            <button
              type="button"
              onClick={() => setEditLang('en')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center gap-1.5 ${
                editLang === 'en' 
                  ? 'bg-white text-[#0A0A0A] shadow-sm' 
                  : 'text-[#666666] hover:text-[#0A0A0A]'
              }`}
            >
              🇬🇧 EN
            </button>
          </div>
        </div>
        
        {editLang === 'en' && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
            <Languages size={18} className="text-blue-600" />
            <p className="text-sm text-blue-800">
              Englische Beschreibung für internationale Besucher
            </p>
          </div>
        )}
        
        {editLang === 'de' ? (
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="input-premium w-full h-32 resize-none border border-[#E5E5E5] px-4"
            placeholder="Geben Sie eine detaillierte Beschreibung des Fahrzeugs ein..."
            required
            data-testid="car-description"
          />
        ) : (
          <textarea
            value={formData.description_en}
            onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
            className="input-premium w-full h-32 resize-none border border-[#E5E5E5] px-4"
            placeholder="Enter a detailed description of the vehicle in English..."
            data-testid="car-description-en"
          />
        )}
        
        <p className="text-xs text-[#999999] mt-2">
          {editLang === 'de' 
            ? '* Pflichtfeld - Deutsche Beschreibung wird als Standard verwendet'
            : 'Optional - Falls leer, wird die deutsche Beschreibung angezeigt'
          }
        </p>
      </div>

      {/* Features / Ausstattung */}
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8">
        <h2 className="font-heading text-xl mb-6">Ausstattung</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Object.entries(CAR_FEATURES).map(([categoryKey, category]) => (
            <div key={categoryKey} className="bg-[#F9F9F7] rounded-lg p-4">
              <button
                type="button"
                onClick={() => toggleCategory(categoryKey)}
                className="flex items-center justify-between w-full text-left mb-2"
              >
                <span className="text-sm font-medium flex items-center gap-2">
                  <span>{category.icon}</span>
                  <span>{category.name_de}</span>
                </span>
                {expandedCategories[categoryKey] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              
              {expandedCategories[categoryKey] && (
                <div className="space-y-2 mt-3 border-t border-[#E5E5E5] pt-3">
                  {category.features.map(feature => (
                    <label 
                      key={feature.key}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:text-[#C4A052] transition-colors"
                    >
                      <Checkbox
                        checked={formData.features.includes(feature.key)}
                        onCheckedChange={() => toggleFeature(feature.key)}
                        className="h-4 w-4"
                      />
                      <span>{feature.de}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Selected Features Count */}
        {formData.features.length > 0 && (
          <p className="mt-4 text-sm text-[#666666]">
            {formData.features.length} Ausstattungsmerkmale ausgewählt
          </p>
        )}
      </div>

      {/* Images */}
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl">Bilder</h2>
          <div className="flex items-center bg-[#F9F9F7] rounded-lg p-1">
            <button
              type="button"
              onClick={() => setImageMode('upload')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center gap-1.5 ${
                imageMode === 'upload' 
                  ? 'bg-white text-[#0A0A0A] shadow-sm' 
                  : 'text-[#666666] hover:text-[#0A0A0A]'
              }`}
            >
              <Upload size={14} />
              Upload
            </button>
            <button
              type="button"
              onClick={() => setImageMode('url')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center gap-1.5 ${
                imageMode === 'url' 
                  ? 'bg-white text-[#0A0A0A] shadow-sm' 
                  : 'text-[#666666] hover:text-[#0A0A0A]'
              }`}
            >
              <Link size={14} />
              URL Links
            </button>
          </div>
        </div>
        
        {/* Upload Mode */}
        {imageMode === 'upload' && (
          <div className="border-2 border-dashed border-[#E5E5E5] p-8 text-center mb-6">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
              data-testid="car-image-upload"
            />
            <label
              htmlFor="image-upload"
              className="cursor-pointer inline-flex flex-col items-center"
            >
              {uploading ? (
                <div className="spinner mb-2" />
              ) : (
                <Upload size={32} className="text-[#999999] mb-2" />
              )}
              <p className="text-[#666666]">
                {uploading ? 'Wird hochgeladen...' : 'Klicken zum Hochladen'}
              </p>
              <p className="text-sm text-[#999999] mt-1">
                PNG, JPG, GIF bis zu 10MB
              </p>
            </label>
          </div>
        )}

        {/* URL Mode */}
        {imageMode === 'url' && (
          <div className="mb-6 space-y-3">
            <p className="text-sm text-[#666666] mb-4">
              Fügen Sie direkte Bild-Links von Ihrem eigenen Server oder Hosting-Service hinzu.
            </p>
            
            {imageUrls.map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Link size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999]" />
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => updateUrlField(index, e.target.value)}
                    className="input-premium w-full pl-10 border border-[#E5E5E5]"
                    placeholder="https://example.com/image.jpg"
                    data-testid={`image-url-${index}`}
                  />
                </div>
                {imageUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrlField(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            ))}
            
            <div className="flex items-center gap-3 mt-4">
              <button
                type="button"
                onClick={addUrlField}
                className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#999999] text-[#666666] hover:border-gold hover:text-gold transition-colors rounded"
              >
                <Plus size={16} />
                Weiteren Link hinzufügen
              </button>
              <Button
                type="button"
                onClick={addUrlsToImages}
                className="btn-primary flex items-center gap-2"
                disabled={!imageUrls.some(url => url.trim())}
              >
                <Image size={16} />
                Bilder hinzufügen
              </Button>
            </div>
            
            <p className="text-xs text-[#999999] mt-2">
              Unterstützte Formate: JPG, PNG, WebP, GIF
            </p>
          </div>
        )}

        {/* Image Preview */}
        {formData.images.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-3 text-[#666666]">
              {formData.images.length} Bild(er) hinzugefügt
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {formData.images.map((url, index) => (
                <div key={index} className="relative group aspect-[4/3]">
                  <img
                    src={getImageUrl(url)}
                    alt={`Car image ${index + 1}`}
                    className="w-full h-full object-cover rounded"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/300x200?text=Bild+nicht+gefunden';
                    }}
                  />
                  {index === 0 && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-gold text-white text-xs rounded">
                      Hauptbild
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-image-${index}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Status */}
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8">
        <h2 className="font-heading text-xl mb-6">Status</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Aktiv</p>
              <p className="text-sm text-[#666666]">Fahrzeug auf der Website anzeigen</p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              data-testid="car-active"
            />
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-[#E5E5E5]">
            <div>
              <p className="font-medium">Hervorgehoben</p>
              <p className="text-sm text-[#666666]">Im Featured-Bereich auf der Startseite anzeigen</p>
            </div>
            <Switch
              checked={formData.is_featured}
              onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
              data-testid="car-featured"
            />
          </div>
          
          {/* Sold Status - Premium Design */}
          <div className="pt-4 border-t border-[#E5E5E5]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full transition-all duration-300 ${
                  formData.is_sold 
                    ? 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/30' 
                    : 'bg-[#F9F9F7]'
                }`}>
                  <BadgeCheck size={20} className={`transition-colors duration-300 ${
                    formData.is_sold ? 'text-white' : 'text-[#999999]'
                  }`} />
                </div>
                <div>
                  <p className="font-medium">Verkauft</p>
                  <p className="text-sm text-[#666666]">Fahrzeug als verkauft markieren</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_sold: !formData.is_sold })}
                className={`relative w-20 h-10 rounded-full transition-all duration-300 overflow-hidden ${
                  formData.is_sold 
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/30' 
                    : 'bg-[#E5E5E5]'
                }`}
                data-testid="car-sold"
              >
                <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                  formData.is_sold 
                    ? 'text-white opacity-100' 
                    : 'text-[#999999] opacity-0'
                }`}>
                  Sold
                </span>
                <span className={`absolute w-8 h-8 bg-white rounded-full shadow-md top-1 transition-all duration-300 flex items-center justify-center ${
                  formData.is_sold ? 'left-11' : 'left-1'
                }`}>
                  {formData.is_sold && <Check size={14} className="text-emerald-600" />}
                </span>
              </button>
            </div>
            
            {formData.is_sold && (
              <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg animate-fadeIn">
                <div className="flex items-center gap-2">
                  <BadgeCheck className="text-emerald-600" size={18} />
                  <span className="text-sm font-medium text-emerald-800">
                    Dieses Fahrzeug wird mit "VERKAUFT" Banner angezeigt
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/admin/cars')}
          className="btn-secondary"
        >
          Abbrechen
        </Button>
        <Button
          type="submit"
          className="btn-primary inline-flex items-center gap-2"
          disabled={saving}
          data-testid="car-submit"
        >
          {saving ? (
            <div className="spinner" />
          ) : (
            <>
              <Check size={16} />
              {isEditing ? 'Speichern' : 'Erstellen'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
